import React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Download,
  BookOpen,
  ClipboardCheck,
  ShieldCheck
} from "lucide-react";
import Navbar from "../../components/Navbar";

const downloads = [
  {
    title: "ISO Certification Overview Guide",
    category: "ISO Standards",
    icon: ShieldCheck,
    fileType: "PDF",
    image: "https://images.unsplash.com/photo-1581092919534-5f8f3a0b59b2",
    description:
      "A comprehensive guide explaining ISO certification benefits, requirements, and implementation roadmap."
  },
  {
    title: "HACCP & Food Safety Checklist",
    category: "Food Safety",
    icon: ClipboardCheck,
    fileType: "PDF",
    image: "https://images.unsplash.com/photo-1600267165460-6f45c1d8b0e6",
    description:
      "Practical HACCP checklist to support food safety compliance and audit readiness."
  },
  {
    title: "Corporate Training Brochure",
    category: "Trainings",
    icon: BookOpen,
    fileType: "PDF",
    image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d",
    description:
      "Overview of MBA Solutions corporate training programs, delivery models, and certification pathways."
  }
];

const Downloads = () => {
  return (
    <>
    <Navbar />
    <section className="relative py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">

      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1498050108023-c5249f4df085"
          alt="Downloads Background"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-gray-50" />
      </div>

      <div className="max-w-7xl mx-auto">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-20"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--bg-primary)] mb-3">
            <FileText size={16} /> Resources Library
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Downloads & Free Resources
          </h2>
          <p className="text-gray-600 text-base md:text-lg">
            Access useful guides, checklists, and brochures to support your
            certification journey, audits, and corporate training initiatives.
          </p>
        </motion.div>

        {/* Download Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {downloads.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -12 }}
                className="group bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute top-4 left-4 text-xs font-semibold px-3 py-1 rounded-full bg-[var(--bg-primary)] text-white">
                    {item.fileType}
                  </span>
                </div>

                {/* Content */}
                <div className="p-7">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <Icon size={14} className="text-[var(--bg-primary)]" />
                    {item.category}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[var(--bg-primary)] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    {item.description}
                  </p>
                  <button className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--bg-primary)] hover:gap-3 transition-all">
                    <Download size={16} /> Download
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24 text-center"
        >
          <button className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all">
            View All Resources
          </button>
        </motion.div>

      </div>
    </section>
    </>
  );
};

export default Downloads;
